/**
 * El alta como puesta en marcha: pasos, no un muro de campos.
 *
 * DE DÓNDE SALE ESTA FORMA
 * ────────────────────────
 * De la puesta en marcha del panel de negocio. Lo que se copia no es el aspecto
 * sino tres decisiones suyas, que son buenas:
 *
 *  1. **Pasos plegables, uno abierto cada vez.** Cuarenta campos a la vez no se
 *     leen: se rellenan a medias.
 *  2. **Cada paso dice qué pasa si falta.** «Sin catálogo puede hablar pero no
 *     reservar» enseña más que un asterisco rojo, y además es verdad.
 *  3. **Arriba, cuánto queda.** No una barra de progreso decorativa: la lista de
 *     lo que impide que el negocio exista.
 *
 * LO QUE AÑADE, Y ES LO QUE AHORRA LA TARDE
 * ─────────────────────────────────────────
 * Describir el negocio en una frase y que se rellene solo lo descriptivo, o
 * partir de una plantilla. Las dos cosas dejan la ficha **propuesta**, nunca
 * guardada: quien da de alta la revisa y decide. Un modelo no da de alta a nadie.
 */
import { useEffect, useState } from 'react'

import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'
import { api } from '../../lib/api'
import type { FichaAlta } from '../../lib/altas'
import { CamposDeLaFicha, COLORES, PASOS, type ClavePaso } from './ficha-campos'

/** Qué le falta a cada paso para estar listo. Vacío = listo. */
function loQueFalta(ficha: FichaAlta, k: ClavePaso): string[] {
    const falta: string[] = []
    const vacio = (v: unknown) => typeof v !== 'string' || v.trim() === ''

    if (k === 'identidad') {
        if (vacio(ficha.nombreVisible)) falta.push('el nombre con el que aparece en el canal')
    }
    if (k === 'catalogo') {
        if (ficha.catalogo.servicios.some((s) => vacio(s.nombre))) falta.push('algún servicio sin nombre')
        if (ficha.catalogo.profesionales.some((p) => vacio(p.nombre))) falta.push('algún profesional sin nombre')
        const prestados = new Set(ficha.catalogo.profesionales.flatMap((p) => p.serviciosIndices))
        if (ficha.catalogo.servicios.some((_, i) => !prestados.has(i))) {
            falta.push('algún servicio que no presta nadie')
        }
    }
    if (k === 'voz') {
        if (vacio(ficha.voz.presentacion)) falta.push('cómo se presenta')
        if (ficha.voz.idiomas.length === 0) falta.push('al menos un idioma (el primero es el de por defecto)')
        for (const [term, v] of Object.entries(ficha.vocabulario)) {
            if (vacio(v)) falta.push(`cómo llama a «${term}»`)
        }
    }
    if (k === 'conocimiento') {
        const hayUbicacion = !vacio(ficha.conocimiento.ubicacion?.direccion)
        const hayFaq = ficha.conocimiento.faq.some((f) => !vacio(f.pregunta) && !vacio(f.respuesta))
        // Solo hace falta si «responder dudas» está encendido de verdad.
        const responde = ficha.capacidades.concedidas.responder && ficha.capacidades.activas.responder
        if (responde && !hayUbicacion && !hayFaq) falta.push('la dirección o alguna pregunta frecuente')
    }
    if (k === 'legal') {
        if (vacio(ficha.legal.privacyPolicyUrl)) falta.push('el enlace a la política de privacidad')
        if (vacio(ficha.legal.privacyPolicyDate)) falta.push('desde cuándo rige esa política')
    }
    return falta
}

export function PuestaEnMarcha({
    ficha,
    onChange,
    nombreNegocio,
    /**
     * Lo que NO vive en la ficha pero se rellena en el mismo paso: los datos de
     * aprovisionamiento, el horario de la agenda, el contacto y el WhatsApp.
     *
     * Van aquí y no en secciones aparte porque tenerlos fuera obligaba a bajar
     * hasta el final de la página para completar algo que el paso ya estaba
     * pidiendo — y a acordarse de que existían.
     */
    extras,
    /** Spec 038/052: Información reutiliza los pasos y omite catálogo. */
    omitir = [],
    /**
     * La propuesta, las plantillas y «este negocio ya puede existir» son del
     * alta. Información ya existe: solo los apartados.
     */
    conPropuesta = true,
}: {
    ficha: FichaAlta
    onChange: (f: FichaAlta) => void
    nombreNegocio: string
    extras?: Partial<Record<ClavePaso, React.ReactNode>>
    omitir?: readonly ClavePaso[]
    conPropuesta?: boolean
}) {
    const [abierto, setAbierto] = useState<ClavePaso | null>('identidad')
    const [descripcion, setDescripcion] = useState('')
    const [proponiendo, setProponiendo] = useState(false)
    const [avisoIa, setAvisoIa] = useState<string | null>(null)
    const [propuestos, setPropuestos] = useState<string[]>([])

    // ── Plantillas ────────────────────────────────────────────────────
    const [plantillas, setPlantillas] = useState<Array<{ id: number; nombre: string; sector: string | null; ficha: Partial<FichaAlta> }>>([])
    const [nombrePlantilla, setNombrePlantilla] = useState('')
    const [avisoPlantilla, setAvisoPlantilla] = useState<string | null>(null)

    // ── Regenerar un apartado suelto ──────────────────────────────────
    const [pidiendoA, setPidiendoA] = useState<ClavePaso | null>(null)
    const [instruccion, setInstruccion] = useState<Record<string, string>>({})
    const [regenerando, setRegenerando] = useState<ClavePaso | null>(null)

    /**
     * Rehacer SOLO un apartado, con lo que el operador pida.
     *
     * Es lo que de verdad pasa montando una demo: uno se da cuenta a mitad de
     * que quería otra cosa en una parte, y no quiere empezar de cero ni perder
     * lo que ya revisó. Por eso viaja también lo que ya hay: para que la
     * regeneración no lo contradiga.
     */
    async function regenerar(k: ClavePaso, bloques: string) {
        setRegenerando(k)
        setAvisoIa(null)
        try {
            const r = await api.post<{ ok: boolean; ficha?: Partial<FichaAlta>; motivo?: string }>(
                '/altas/propuesta',
                {
                    descripcion: descripcion || `Negocio: ${nombreNegocio || 'sin nombre'}`,
                    nombre: nombreNegocio || undefined,
                    soloBloques: bloques.split(','),
                    instruccion: instruccion[k] || undefined,
                    yaPuesto: ficha,
                },
            )
            if (!r.ok || !r.ficha) {
                setAvisoIa(r.motivo ?? 'No se pudo rehacer este apartado.')
                return
            }
            onChange({ ...ficha, ...r.ficha })
            setPidiendoA(null)
        } catch {
            setAvisoIa('No se pudo rehacer este apartado.')
        } finally {
            setRegenerando(null)
        }
    }

    useEffect(() => {
        void api
            .get<{ plantillas: typeof plantillas }>('/altas/plantillas')
            .then((r) => setPlantillas(r.plantillas))
            .catch(() => setPlantillas([]))
    }, [])

    /**
     * Partir de una plantilla.
     *
     * Se MEZCLA sobre lo que hay, igual que la propuesta: nunca sustituye. Y una
     * plantilla no trae identidad —ni cuenta, ni calendario, ni el nombre en el
     * canal— porque se guardó sin ella: lo reutilizable no puede llevar dentro a
     * quién perteneció.
     */
    function partirDe(id: number) {
        const p = plantillas.find((x) => x.id === id)
        if (!p) return
        onChange({ ...ficha, ...p.ficha })
        setAvisoPlantilla(`Partiendo de «${p.nombre}». Revisa y completa lo que le falte.`)
        setAbierto('identidad')
    }

    async function guardarComoPlantilla() {
        if (nombrePlantilla.trim().length < 2) return
        setAvisoPlantilla(null)
        try {
            const r = await api.post<{ id: number; retirado: string[] }>('/altas/plantillas', {
                nombre: nombrePlantilla.trim(),
                ficha,
            })
            // Se le enseña QUÉ se quitó: que sepa que su cuenta y su calendario
            // no viajan es lo que evita que luego se sorprenda de tener que
            // ponerlos otra vez.
            setAvisoPlantilla(
                r.retirado.length > 0
                    ? `Guardada. No viaja lo propio de este negocio: ${r.retirado.join(', ')}.`
                    : 'Guardada.',
            )
            setNombrePlantilla('')
            const l = await api.get<{ plantillas: typeof plantillas }>('/altas/plantillas')
            setPlantillas(l.plantillas)
        } catch {
            setAvisoPlantilla('No se pudo guardar la plantilla.')
        }
    }

    const visibles = PASOS.filter((p) => !omitir.includes(p.k))
    const estado = visibles.map((p) => ({ ...p, falta: loQueFalta(ficha, p.k) }))
    const pendientes = estado.filter((p) => p.req && p.falta.length > 0)
    const listos = estado.filter((p) => p.falta.length === 0).length

    async function pedirPropuesta() {
        setProponiendo(true)
        setAvisoIa(null)
        try {
            const r = await api.post<{
                ok: boolean
                ficha?: Partial<FichaAlta>
                motivo?: string
                corregido?: string[]
                origen?: { modeloServido: string }
            }>('/altas/propuesta', { descripcion, nombre: nombreNegocio || undefined })
            if (!r.ok || !r.ficha) {
                setAvisoIa(r.motivo ?? 'No se pudo proponer. Puedes rellenarlo a mano.')
                return
            }
            // Se MEZCLA sobre lo que ya hay, no se sustituye: si el operador ya
            // escribió algo a mano, una propuesta no se lo puede llevar por
            // delante.
            onChange({ ...ficha, ...r.ficha })
            setPropuestos(Object.keys(r.ficha))
            setAvisoIa(null)
            setAbierto('voz')
        } catch {
            setAvisoIa('No se pudo proponer. Puedes rellenarlo a mano: no se pierde nada.')
        } finally {
            setProponiendo(false)
        }
    }

    return (
        <div className="space-y-4">
            {conPropuesta && (
            <>
            {/* ── Describe el negocio y se rellena solo ───────────────── */}
            <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-4">
                <div className="flex items-start gap-2">
                    <span className="text-lg">✨</span>
                    <div className="min-w-0 flex-1 space-y-2">
                        <div>
                            <Label htmlFor="describe">Descríbelo en una frase y se rellena solo</Label>
                            <p className="mt-0.5 text-xs text-neutral-500">
                                Solo la parte descriptiva — cómo habla, qué puede afirmar, cuándo pasa a
                                una persona. Ni la cuenta, ni el calendario, ni lo que el plan incluye:
                                eso no se propone. <strong>Nada se guarda hasta que tú lo apruebes.</strong>
                            </p>
                        </div>
                        <Textarea
                            id="describe"
                            rows={2}
                            placeholder="Una clínica dental pequeña en Las Palmas, dos doctores, trato de usted…"
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                        />
                        <div className="flex items-center gap-3">
                            <Button
                                type="button"
                                size="sm"
                                disabled={proponiendo || descripcion.trim().length < 10}
                                onClick={() => void pedirPropuesta()}
                            >
                                {proponiendo ? 'Pensando…' : 'Rellenar con IA'}
                            </Button>
                            {propuestos.length > 0 && (
                                <span className="text-xs text-emerald-400">
                                    Propuesto: {propuestos.join(', ')} — revísalo antes de emitir
                                </span>
                            )}
                            {avisoIa && <span className="text-xs text-amber-400">{avisoIa}</span>}
                        </div>

                        {/* ── O partir de algo ya hecho ────────────────── */}
                        <div className="flex flex-wrap items-end gap-3 border-t border-neutral-800 pt-3">
                            {plantillas.length > 0 && (
                                <div className="space-y-1.5">
                                    <Label htmlFor="plantilla">O parte de una plantilla</Label>
                                    <select
                                        id="plantilla"
                                        className="flex h-9 w-64 rounded-md border border-neutral-700 bg-neutral-900 px-3 py-1 text-sm text-neutral-100"
                                        defaultValue=""
                                        onChange={(e) => e.target.value && partirDe(Number(e.target.value))}
                                    >
                                        <option value="">Elegir…</option>
                                        {plantillas.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.nombre}
                                                {p.sector ? ` · ${p.sector}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <Label htmlFor="guardarPlantilla">Guardar esto como plantilla</Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="guardarPlantilla"
                                        className="w-56"
                                        placeholder="Peluquería tipo"
                                        value={nombrePlantilla}
                                        onChange={(e) => setNombrePlantilla(e.target.value)}
                                    />
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={nombrePlantilla.trim().length < 2}
                                        onClick={() => void guardarComoPlantilla()}
                                    >
                                        Guardar plantilla
                                    </Button>
                                </div>
                            </div>
                            {avisoPlantilla && (
                                <span className="text-xs text-neutral-400">{avisoPlantilla}</span>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Cuánto le falta ─────────────────────────────────────── */}
            <section
                className={`rounded-lg border p-4 ${
                    pendientes.length === 0
                        ? 'border-emerald-900/60 bg-emerald-950/30'
                        : 'border-neutral-800 bg-neutral-900/40'
                }`}
            >
                <h2 className="text-sm font-semibold text-neutral-100">
                    {pendientes.length === 0
                        ? 'Este negocio ya puede existir'
                        : 'Este negocio todavía no puede existir'}
                </h2>
                <p className="mt-1 text-xs text-neutral-400">
                    {pendientes.length === 0
                        ? 'Todo lo obligatorio está puesto. Puedes previsualizar y emitir.'
                        : `Le falta${pendientes.length > 1 ? 'n' : ''} ${pendientes.length} cosa${
                              pendientes.length > 1 ? 's' : ''
                          }: ${pendientes.map((p) => p.t.toLowerCase()).join(', ')}.`}
                </p>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-neutral-800">
                    <div
                        className="h-full rounded-full bg-emerald-500 transition-all"
                        style={{ width: `${Math.round((listos / Math.max(estado.length, 1)) * 100)}%` }}
                    />
                </div>
            </section>
            </>
            )}

            {/* ── Los pasos ───────────────────────────────────────────── */}
            <div className="space-y-2">
                {estado.map((p) => {
                    const listo = p.falta.length === 0
                    const esteAbierto = abierto === p.k
                    const c = COLORES[p.color] ?? COLORES['slate']!
                    return (
                        <section
                            key={p.k}
                            className={`overflow-hidden rounded-lg border ${c.borde} ${c.fondo}`}
                        >
                            <button
                                type="button"
                                data-testid={`paso-${p.k}`}
                                className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-neutral-900/60"
                                onClick={() => setAbierto(esteAbierto ? null : p.k)}
                                aria-expanded={esteAbierto}
                            >
                                <span
                                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${c.borde} bg-neutral-950/60 text-base`}
                                >
                                    {p.ic}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="flex items-center gap-2">
                                        <span className={`text-sm font-semibold ${c.texto}`}>{p.t}</span>
                                        {!p.req && (
                                            <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] text-neutral-400">
                                                opcional
                                            </span>
                                        )}
                                    </span>
                                    {/* Lo que pasa si falta, en vez de un asterisco. */}
                                    <span className="mt-0.5 block text-xs text-neutral-500">
                                        {listo ? p.sin : p.falta.length ? `Falta ${p.falta.join(', ')}.` : p.sin}
                                    </span>
                                </span>
                                {listo ? (
                                    <span className="mt-0.5 flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-300">
                                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[10px] text-neutral-950">
                                            ✓
                                        </span>
                                        completo
                                    </span>
                                ) : (
                                    <span
                                        className={`mt-0.5 shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
                                            p.req
                                                ? 'bg-amber-500/15 text-amber-300'
                                                : 'bg-neutral-800 text-neutral-400'
                                        }`}
                                    >
                                        {p.req ? 'falta algo' : 'opcional'}
                                    </span>
                                )}
                            </button>
                            {esteAbierto && (
                                <div className={`border-t ${c.borde} px-4 py-4`}>
                                    {conPropuesta && p.ia && (
                                        <div className="mb-4 rounded-md border border-neutral-800 bg-neutral-950/50 p-3">
                                            {pidiendoA === p.k ? (
                                                <div className="space-y-2">
                                                    <Label htmlFor={`ia-${p.k}`}>
                                                        ¿Qué quieres en este apartado?
                                                    </Label>
                                                    <Textarea
                                                        id={`ia-${p.k}`}
                                                        rows={2}
                                                        placeholder={p.ejemplo}
                                                        value={instruccion[p.k] ?? ''}
                                                        onChange={(e) =>
                                                            setInstruccion({ ...instruccion, [p.k]: e.target.value })
                                                        }
                                                    />
                                                    <div className="flex gap-2">
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            disabled={regenerando === p.k}
                                                            onClick={() => void regenerar(p.k, p.ia!)}
                                                        >
                                                            {regenerando === p.k ? 'Rehaciendo…' : 'Rehacer este apartado'}
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => setPidiendoA(null)}
                                                        >
                                                            Cancelar
                                                        </Button>
                                                    </div>
                                                    <p className="text-xs text-neutral-500">
                                                        Solo se rehace esto. Lo demás que ya hayas revisado se queda.
                                                    </p>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="text-xs text-neutral-400 underline hover:text-neutral-200"
                                                    onClick={() => setPidiendoA(p.k)}
                                                >
                                                    ✨ Rehacer este apartado con IA, diciéndole qué quieres
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <CamposDeLaFicha ficha={ficha} onChange={onChange} paso={p.k} />
                                    {extras?.[p.k]}
                                </div>
                            )}
                        </section>
                    )
                })}
            </div>
        </div>
    )
}
